import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Link from '@cloudscape-design/components/link';
import { useAuth } from '@vextura/shell';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ssoAuthorizeUrl } from './auth-adapter.js';

/**
 * Login screen. Two paths:
 *   1. Vextura SSO — starts the OIDC Authorization Code flow via vex-id
 *      (/authorize → /login → /consent → /sso/callback). Only shown when
 *      /.well-known/openid-configuration probes 2xx on mount, so clusters
 *      without vex-id don't offer a button that dead-ends at 503.
 *   2. Username / password (client_credentials against vex-auth) — always
 *      available, used for admin ops and clusters where SSO isn't wired.
 *
 * Also rendered when the JWT expires mid-session (AuthProvider's watchdog
 * clears the token). In that case ?reason=session_expired is set and we
 * show a "your session ended" banner so the user knows why they're back.
 */
export function LoginScreen(): ReactNode {
  const { signIn, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 'probing' = still checking, 'ready' = SSO usable, 'unavailable' = hide the button
  const [ssoState, setSsoState] = useState<'probing' | 'ready' | 'unavailable'>('probing');

  const sessionExpired = searchParams.get('reason') === 'session_expired';
  const ssoError = searchParams.get('sso_error');
  const returnTo = searchParams.get('return_to') ?? '/';

  // Auto-leave /signin once the user is authenticated. Covers both the
  // fresh-sign-in path (button click → AuthProvider updates → this effect
  // fires) and the "already signed in but hit /signin manually" path.
  // replace=true so /signin doesn't sit in browser history.
  useEffect(() => {
    if (user) navigate(returnTo, { replace: true });
  }, [user, returnTo, navigate]);

  // Probe OIDC discovery on mount. A 200 + valid JSON with an issuer field
  // means vex-id is wired up on this cluster. Anything else (503, 404,
  // non-JSON, invalid shape) hides the SSO button so users don't hit a dead
  // /authorize endpoint. Timeout after ~3s so a hung endpoint doesn't
  // freeze the login screen indefinitely.
  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();
    const timer = window.setTimeout(() => ac.abort(), 3000);
    fetch('/.well-known/openid-configuration', { signal: ac.signal })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = (await r.json()) as { issuer?: string; authorization_endpoint?: string };
        if (!j.issuer || !j.authorization_endpoint) throw new Error('discovery missing fields');
        if (!cancelled) setSsoState('ready');
      })
      .catch(() => {
        if (!cancelled) setSsoState('unavailable');
      })
      .finally(() => window.clearTimeout(timer));
    return () => {
      cancelled = true;
      ac.abort();
      window.clearTimeout(timer);
    };
  }, []);

  const onSubmit = async () => {
    if (!username || !password) {
      setError('Username and password are required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await signIn(username, password);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const onSsoClick = () => {
    // Full-page navigate — OIDC authorize is a browser redirect the SPA
    // cannot intercept. Any anti-CSRF `state` is written to sessionStorage
    // by ssoAuthorizeUrl() and read back in /sso/callback.
    window.location.href = ssoAuthorizeUrl();
  };

  return (
    <Box padding="xxxl">
      <div style={{ maxWidth: 420, margin: '120px auto 0' }}>
        <Container
          header={
            <Header variant="h1" description="Sign in to your Vextura tenant">
              Vextura Console
            </Header>
          }
        >
          <SpaceBetween size="l">
            {sessionExpired ? (
              <Alert type="warning" header="Your session ended">
                Your access token expired. Sign in again to continue.
              </Alert>
            ) : null}
            {ssoError ? (
              <Alert type="error" header="SSO sign-in failed">
                {ssoError}
              </Alert>
            ) : null}

            {ssoState === 'ready' ? (
              <>
                <Button variant="primary" fullWidth onClick={onSsoClick}>
                  Sign in with Vextura SSO
                </Button>
                <Box textAlign="center" color="text-body-secondary" fontSize="body-s">
                  — or use username &amp; password —
                </Box>
              </>
            ) : ssoState === 'unavailable' ? (
              <Alert type="info">
                SSO is not configured on this cluster — sign in with username &amp; password below.
              </Alert>
            ) : null /* probing → render nothing (keep the layout stable) */}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                void onSubmit();
              }}
            >
              <Form
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Link
                      onFollow={(evt) => {
                        evt.preventDefault();
                        navigate('/signup');
                      }}
                      href="/signup"
                    >
                      Create a tenant
                    </Link>
                    <Button
                      variant={ssoState === 'ready' ? 'normal' : 'primary'}
                      formAction="submit"
                      loading={submitting}
                    >
                      Sign in
                    </Button>
                  </SpaceBetween>
                }
              >
                <SpaceBetween size="m">
                  {error ? <Alert type="error">{error}</Alert> : null}
                  <FormField label="Username">
                    <Input
                      value={username}
                      autoFocus
                      onChange={({ detail }) => setUsername(detail.value)}
                    />
                  </FormField>
                  <FormField label="Password">
                    <Input
                      value={password}
                      type="password"
                      onChange={({ detail }) => setPassword(detail.value)}
                    />
                  </FormField>
                </SpaceBetween>
              </Form>
            </form>
          </SpaceBetween>
        </Container>
      </div>
    </Box>
  );
}
